import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Mail, User as UserIcon } from 'lucide-react';
import Spinner from '@/components/ui/Spinner';
import {
  getChatbotEnabled,
  setChatbotEnabledAdmin,
  getChatbotChannels,
  setChannelChatbotOverride,
} from '@/services/automationService';
import { fetchUsers } from '@/services/authService';

const PAGE_SIZE = 20;

export default function ChatbotSettingsManage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data: chatbotEnabledData, isLoading: isLoadingChatbotEnabled } = useQuery({
    queryKey: ['chatbotEnabledAdmin'],
    queryFn: getChatbotEnabled,
  });
  const chatbotEnabled = chatbotEnabledData?.chatbotEnabled ?? true;

  const { mutate: toggleChatbotEnabled, isPending: isTogglingChatbotEnabled } = useMutation({
    mutationFn: setChatbotEnabledAdmin,
    onSuccess: (data) => {
      queryClient.setQueryData(['chatbotEnabledAdmin'], data);
      toast.success(t('chatbotEnabledUpdatedSuccess'));
    },
    onError: () => {
      toast.error(t('chatbotEnabledUpdatedError'));
    },
  });

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const { data: users } = useQuery({
    queryKey: ['users'],
    queryFn: fetchUsers,
  });

  const usersById = useMemo(() => {
    const map = new Map<string, { name: string | null; email: string }>();
    users?.forEach((user) => {
      map.set(user.id, { name: user.name, email: user.email });
    });
    return map;
  }, [users]);

  // Buscar por nombre/email de usuario se resuelve del lado del cliente:
  // si el texto tipeado matchea el name/email de algún usuario ya cargado
  // (mismo usersById de arriba), se manda el userId de ese usuario como
  // `search` al backend. Si no matchea a ningún usuario, se manda el texto
  // tal cual para que el backend lo intente contra nombre/teléfono/id de canal.
  const resolvedSearch = useMemo(() => {
    const trimmed = search.trim();
    if (!trimmed) return undefined;
    const lower = trimmed.toLowerCase();
    const matchedUser = users?.find(
      (user) =>
        user.email.toLowerCase().includes(lower) ||
        (user.name && user.name.toLowerCase().includes(lower)),
    );
    return matchedUser ? matchedUser.id : trimmed;
  }, [search, users]);

  const {
    data: chatbotChannelsPage,
    isLoading: isLoadingChatbotChannels,
    isError: isChatbotChannelsError,
  } = useQuery({
    queryKey: ['chatbotChannels', page, resolvedSearch],
    queryFn: () => getChatbotChannels({ page, pageSize: PAGE_SIZE, search: resolvedSearch }),
  });

  const { mutate: toggleOverride, variables: togglingVariables, isPending: isTogglingOverride } = useMutation({
    mutationFn: ({ channelId, enabled }: { channelId: number; enabled: boolean }) =>
      setChannelChatbotOverride(channelId, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chatbotChannels'] });
      toast.success(t('chatbotOverrideUpdatedSuccess'));
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message || t('chatbotOverrideUpdatedError');
      toast.error(msg);
    },
  });

  const handleSearch = () => {
    setSearch(searchInput.trim());
    setPage(1);
  };

  const channelItems = chatbotChannelsPage?.items ?? [];

  return (
    <div className="p-6 w-full">
      {(isLoadingChatbotEnabled || isTogglingChatbotEnabled) && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <Spinner size="lg" color="primary" />
        </div>
      )}
      <div className="max-w-3xl space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('chatbotSettingsTitle')}</h1>
          <p className="text-sm text-gray-600">{t('chatbotSettingsDescription')}</p>
        </div>

        <section className="space-y-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{t('chatbotEnabledTitle')}</h2>
              <p className="text-sm text-gray-600">{t('chatbotEnabledDescription')}</p>
            </div>
            {isLoadingChatbotEnabled ? (
              <p className="text-sm text-gray-500">{t('loading')}</p>
            ) : (
              <button
                type="button"
                onClick={() => toggleChatbotEnabled(!chatbotEnabled)}
                disabled={isTogglingChatbotEnabled}
                aria-pressed={chatbotEnabled}
                aria-label={t('chatbotEnabledTitle')}
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
                  chatbotEnabled ? 'bg-indigo-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    chatbotEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            )}
          </div>
          {!chatbotEnabled && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
              {t('chatbotEnabledWarning')}
            </p>
          )}
        </section>

        <section className="space-y-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('chatbotChannelOverrideTitle')}</h2>
            <p className="text-sm text-gray-600">{t('chatbotChannelOverrideDescription')}</p>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSearch();
              }}
              placeholder={t('chatbotChannelSearchPlaceholder')}
              className="w-full max-w-sm rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-green-200 outline-none"
            />
            <button
              type="button"
              onClick={handleSearch}
              className="px-4 py-2 rounded text-white bg-indigo-600 hover:bg-indigo-700"
            >
              {t('search')}
            </button>
          </div>

          {isLoadingChatbotChannels && <p className="text-sm text-gray-500">{t('loading')}</p>}
          {isChatbotChannelsError && (
            <p className="text-sm text-red-600">{t('errorLoadingChatbotChannels')}</p>
          )}

          {!isLoadingChatbotChannels && !isChatbotChannelsError && (
            <div className="overflow-hidden rounded-lg border border-gray-200">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        {t('userName')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        {t('channels')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        {t('status')}
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                        {t('chatbotChannelExceptionColumn')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {channelItems.map((row) => {
                      const user = usersById.get(row.userId);
                      const statusLabel = row.linearActive
                        ? t('chatbotStatusLinearActive')
                        : row.aiActive
                          ? t('chatbotStatusAiActive')
                          : t('chatbotStatusNone');
                      const isRowToggling =
                        isTogglingOverride && togglingVariables?.channelId === row.id;
                      return (
                        <tr key={row.id} className="transition hover:bg-gray-50">
                          <td className="whitespace-nowrap px-6 py-4">
                            <div className="flex items-center">
                              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100">
                                <UserIcon className="h-5 w-5 text-indigo-600" />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {user?.name || t('noName') || row.userId}
                                </div>
                                <div className="flex items-center text-xs text-gray-500">
                                  <Mail className="mr-1 h-3 w-3 text-gray-400" />
                                  {user?.email || t('notAvailable')}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                            {row.nameChannel || row.phoneChannel || `#${row.id}`}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                            {statusLabel}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-right">
                            <button
                              type="button"
                              onClick={() =>
                                toggleOverride({ channelId: row.id, enabled: !row.overrideEnabled })
                              }
                              disabled={isRowToggling}
                              aria-pressed={row.overrideEnabled}
                              aria-label={t('chatbotChannelExceptionColumn')}
                              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
                                row.overrideEnabled ? 'bg-indigo-600' : 'bg-gray-300'
                              }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  row.overrideEnabled ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {channelItems.length === 0 && (
                <div className="py-12 text-center">
                  <p className="text-gray-500">{t('noChatbotChannelsFound')}</p>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2">
            <button
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-50"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              {t('previous')}
            </button>
            <span className="text-sm text-gray-600">
              {t('page')} {page}
            </span>
            <button
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-50"
              disabled={channelItems.length < PAGE_SIZE}
              onClick={() => setPage((p) => p + 1)}
            >
              {t('next')}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
